import { useState, type ReactNode } from 'react';
import { Account } from './Account.tsx';
import { Billing } from './Billing.tsx';
import { Branding } from './Branding.tsx';
import { Delivery } from './Delivery.tsx';
import { Users } from './Users.tsx';
import { can, type Capability, type MeView } from './api.ts';

/**
 * Deployment settings, plus the two surfaces about people.
 *
 * Branding and delivery configure the deployment rather than a client: one MSP
 * behind one deployment, so there is nothing to scope either of them to. People
 * and Your account joined them because both are settings in the same sense —
 * they are about this deployment rather than about anybody's tenant.
 *
 * A tab is offered only if the signed-in person holds the capability it needs.
 * That is a convenience and not the control: every route behind every one of
 * these tabs is authorised again on the server, and hiding a tab saves a
 * pointless 403 rather than preventing anything.
 */
interface SettingsTab {
  readonly id: string;
  readonly label: string;
  /** Null means everybody who can sign in. */
  readonly requires: Capability | null;
  readonly render: (me: MeView) => ReactNode;
}

const TABS: readonly SettingsTab[] = [
  {
    id: 'branding',
    label: 'Branding',
    requires: 'settings.write',
    render: () => <Branding />,
  },
  {
    id: 'delivery',
    label: 'Delivery',
    requires: 'settings.write',
    render: () => <Delivery />,
  },
  {
    id: 'people',
    label: 'People',
    requires: 'users.manage',
    render: (me) => <Users me={me} />,
  },
  {
    /*
     * Everybody, not `settings.write` or `billing.manage`. This is the one tab
     * that is purely a statement of fact about the account — what each client
     * costs, what the package allows and what is left of it — and the engineer
     * about to connect a fifth client is exactly the person who needs to see
     * that four is the limit, before the refusal. The buttons that spend money
     * are gated inside it, to owners.
     */
    id: 'billing',
    label: 'Billing',
    requires: null,
    render: (me) => <Billing me={me} />,
  },
  {
    id: 'account',
    label: 'Your account',
    requires: null,
    render: (me) => <Account me={me} />,
  },
];

interface SettingsProps {
  readonly me: MeView;
}

export const Settings = ({ me }: SettingsProps) => {
  const visible = TABS.filter((tab) => tab.requires === null || can(me, tab.requires));
  // Everybody holds at least "Your account", so there is always a first tab.
  const fallback = visible[0] ?? TABS[TABS.length - 1]!;
  const [active, setActive] = useState<string>(fallback.id);
  const current = visible.find((tab) => tab.id === active) ?? fallback;

  return (
    <div className="settings">
      <nav className="tabs-underline" aria-label="Settings">
        {visible.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className="tab"
            aria-current={tab.id === current.id ? 'page' : undefined}
            onClick={() => setActive(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <div className="settings-panel">{current.render(me)}</div>
    </div>
  );
};
